<template>
  <div class="borrowerAttributeDate">
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :tooltipText="tooltipText"
      :helpText="helpText"
      :disclaimer="disclaimer"
      :errorText="errorText">
      <template v-slot:input>
        <div class="form-floating">
          <input
            :id="'ba' + identifier"
            :name="'ba' + identifier"
            :min="minDateString"
            :max="maxDateString"
            class="form-control"
            type="date"
            :class="{
              'is-invalid': v$.$invalid && v$.$dirty,
              'disabled': disabled,
              'disabled-repressed': disabled && repressDisabledStyle
            }"
            :disabled="disabled"
            :placeholder="label"
            :aria-label="label"
            :required="questionRequired"
            :value="value"
            @blur="onBlur(v$, $event, question.submitImmediately || false)"
            @change="onChange(v$, $event, question.submitImmediately || false)"
            :data-cy="question.alias"
            :dreams-id="question.alias"
          />
          <label :for="'ba' + identifier"><span>{{ label }}</span></label>
          <LendioBootstrapTooltip v-if="tooltipText">
            <span class="text-tooltip-icon" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
              <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
            </span>
          </LendioBootstrapTooltip>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
  import BaseBorrowerAttributeMixin from './BaseBorrowerAttributeMixin'
  import { isValid } from 'date-fns'
  import { useVuelidate } from '@vuelidate/core'
  import { between, requiredIf } from '@vuelidate/validators'
  import { format } from 'date-fns'

  export default {
    props: {
      disabled: Boolean,
      repressDisabledStyle: Boolean
    },
    emits: ['invalidity', 'answer'],
    setup: () => ({ v$: useVuelidate() }),
    data () {
      return {
        // In the template, we need to use :value instead of v-model. The html
        // date input stores the date as 'YYYY-MM-DD' string but we want to
        // validate it as a Date object to use ranges (like between).
        // v-model doesn't give us a chance to switch it back and forth. Since
        // it's "bind"ed to the DOM element, it instantly tries to validate
        // as a string when we need it to validate as a Date object.
        date: null,     // Date object.
        // This is the :value we assign to the input and persist in
        // this.question.value by emitting it back to parent component.
        value: '', // String.

        // Date range defaults (if not passed via question object).

        // minDate - Default to 100 years back (potential birth dates).
        // Overrideable via question.minDate.
        minDate: new Date(Date.now() - (100 * 365 * 24 * 60 * 60 * 1000)),
        // String version needed for input min attribute to constrain
        // selections in date popup.
        minDateString: '',

        // maxDate - Defaults to 10 years ahead by adding milliseconds to the
        // now() timestame (e.g. 10 years * 365 days * 24 hours, etc.).
        // Overrideable via question.maxDate.
        maxDate: new Date(Date.now() + (10 * 365 * 24 * 60 * 60 * 1000)),
        // String version needed for input max attribute to constrain
        // selections in date popup.
        maxDateString: '',
      }
    },

    created: function () {
      // Confirm the question.value (a date String) is a valid date when
      // converted to a Date. If so, populate the input's :value with it.
      // The input will display the date according to local format
      // (i.e 'MM-DD-YYYY' in the U.S.) but in the background it's saved as
      // YYYY-MM-DD.
      if (isValid(new Date(this.question.value))) {
        this.value = this.question.value
        this.date = new Date(this.question.value)
      }

      // If provided, override the default min and max dates. We pass a
      // string via the question to ignore timezone diffs between client and
      // server.
      if (this.question.minDate) {
        this.minDate = new Date(this.question.minDate)
      }
      this.minDateString = this.question.minDate ? this.question.minDate : format(this.minDate, 'yyyy-MM-dd')

      if (this.question.maxDate) {
        this.maxDate = new Date(this.question.maxDate)
      }
      this.maxDateString = this.question.maxDate ? this.question.maxDate : format(this.maxDate, 'yyyy-MM-dd')

      this.$watch('invalid', (invalid) => {
        this.$emit('invalidity', invalid)
      }, {immediate: true})

    },

    mixins: [BaseBorrowerAttributeMixin],

    validations: function () {
      return {
        // We validate on this.date to use date validation features not
        // available on strings, like between.
        date: {
          required: requiredIf(() => !this.question.mustAllowNull),
          between: between(this.minDate, this.maxDate)
        }
      }
    },

    computed: {
      invalid() {
        return this.v$.$invalid
      },
      // HTML date fields have a calendar icon where our tooltip is placed.
      // Only show tooltips for disabled fields (where the calendar icon is
      // hidden).
      // This works for our current use case (funding needs) but we need a
      // styleguide option for showing tooltips in active date fields.
      // TODO: Determine how/where to show tooltip data on dates.
      tooltipText() {
        return this.question.tooltip && this.disabled
          ? this.question.tooltip
          : ''
      },
      errorText() {
        let errorText = ''
        if (!this.v$.date.between) {
          // Date range-specific error.
          errorText = `Enter a date between ${this.minDateString} and ${this.maxDateString}.`
        } else if (this.v$.date.$error) {
          // Catch-all date error.
          errorText = 'Please enter a valid date'
        }

        return errorText
      },
    },

    methods: {
      // Validate dates (function below) for both blur and change to ensure
      // the input gives timely feedback.
      onBlur(v$, e, submitImmediately) {
        this.validateDates(v$, e, submitImmediately)
      },
      onChange(v$, e, submitImmediately) {
        this.validateDates(v$, e, submitImmediately)
      },

      validateDates(v$, e, submitImmediately) {
        // Convert the string version of the date ('YYYY-MM-DD') from the date
        // input back to a Date object for validation. We validate
        // this.date but we persist this.value.

        // Update our two data values with input value.
        this.value = e.target.value
        this.date = new Date(this.value)

        // Manually trigger evaluation of date's validity (as a Date). v$ has
        // the variables defined in the validations section. We "touch"
        // this.date, not this.value.
        v$.$touch()

        // If the converted date string is a valid date, emit it as the answer
        // to save it in the vuex store as a date string.
        this.$emit('answer', {
          value: this.value,
          isInvalid: v$.$invalid,
          submitImmediately
        })
      }
    }
  }
</script>

<style lang="scss" scoped>

.no-select {
  color: $neutral-500;
}

.form-floating {
  label {
    color: $type-400;
  }
}

.text-tooltip-icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: $primary;
}

// Don't pad right in date inputs. Looks wrong going beyond calendar icon.
.was-validated .form-control:invalid, .form-control.is-invalid {
  padding-right: 1rem;
}
</style>
